import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import DataApi from '../../api/DataApi';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import { escapeCsvValue, parseCSVText } from '../../utils/csvParseUtils';

/** Imports with this many rows or more run as a background job (server emails when done). */
const IMPORT_ASYNC_THRESHOLD = 50;
const IMPORT_JOB_POLL_MS = 4000;
const IMPORT_JOB_TERMINAL = ['completed', 'failed', 'cancelled'];

const ImportDataModal = ({
  show,
  onClose,
  objectName,
  allObjectFields = [],
  columns = [],
  onRefresh,
}) => {
  const [importData, setImportData] = useState([]);
  const [importFileName, setImportFileName] = useState('');
  const [importProgress, setImportProgress] = useState({
    total: 0,
    processed: 0,
    created: 0,
    updated: 0,
    errors: 0,
  });
  const [importResult, setImportResult] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [showMappingStep, setShowMappingStep] = useState(false);
  const [availableFields, setAvailableFields] = useState([]);
  const [upsertFields, setUpsertFields] = useState([]);
  const [showUpsertStep, setShowUpsertStep] = useState(false);
  const [importMode, setImportMode] = useState('insert'); // 'insert', 'update', 'upsert'
  const [showModeStep, setShowModeStep] = useState(false);
  // Async (background job) import state for large files
  const [asyncJob, setAsyncJob] = useState(null);
  const [asyncCancelling, setAsyncCancelling] = useState(false);
  const asyncPollRef = useRef(null);

  const resolveFieldName = (field) =>
    (field?.value || field?.field_name || '').toLowerCase();
  const isReadOnlyField = (field) =>
    field?.is_readonly === true || field?.read_only === true || field?.readonly === true;
  const isOwnerField = (field) => {
    const fieldName = resolveFieldName(field);
    return fieldName === 'owner' || fieldName === 'ownerid';
  };
  const isRequiredField = (field) => {
    if (importMode === 'update') return false;
    if (!field?.is_required) return false;
    if (isOwnerField(field)) return false;
    if (importMode === 'insert' && resolveFieldName(field) === 'id') return false;
    return true;
  };
  /** Keep import batch payloads small — full DB rows are not needed client-side. */
  const toImportSuccessSummary = item => ({
    id: item?.id,
    action: item?.action,
    importIndex: item?.importIndex,
  });

  const resolveDataType = (field) =>
    (field?.data_type || field?.type || '').toLowerCase();
  const isImportableField = (field, options = {}) => {
    const includeId = options.includeId !== false;
    const fieldName = resolveFieldName(field);
    const dataType = resolveDataType(field);
    if (!fieldName) return false;
    if (fieldName === 'id') return includeId;
    if (['calculated', 'rollupsummary'].includes(dataType)) return false;
    return !isReadOnlyField(field);
  };

  const extractFieldsFromConfig = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload.fields)) return payload.fields;
    if (Array.isArray(payload.data?.fields)) return payload.data.fields;
    if (Array.isArray(payload.data?.data?.fields)) return payload.data.data.fields;
    return [];
  };

  const buildFieldListFromConfig = (fields) =>
    fields.map(field => ({
      value: field.field_name,
      label: field.field_label || field.field_name,
      type: field.data_type || 'text',
      data_type: field.data_type || 'text',
      is_unique: field.is_unique || false,
      is_required: field.is_required || false,
      is_readonly: field.is_readonly || false,
    }));

  const getAllImportFields = async () => {
    if (allObjectFields.length > 0) {
      return allObjectFields;
    }
    if (!objectName) {
      return [];
    }
    try {
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
      const fields = extractFieldsFromConfig(response) || extractFieldsFromConfig(response?.data);
      if (!fields.length) {
        return [];
      }
      return buildFieldListFromConfig(fields);
    } catch (error) {
      console.error('Error fetching object fields for import:', error);
      return [];
    }
  };

  // Auto-match CSV headers to object fields
  const autoMatchFields = (csvHeadersToMatch, availableFieldsToMatch) => {
    const mapping = {};

    csvHeadersToMatch.forEach(csvHeader => {
      // Remove asterisk (*) and any special characters for matching
      // This handles both "First Name *" and "First Name" from CSV
      const cleanedHeader = csvHeader.replace(/\s*\*\s*$/g, '').trim();

      const normalizedCsvHeader = cleanedHeader
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]/g, '');

      // Find best match
      let bestMatch = null;
      let bestScore = 0;

      availableFieldsToMatch.forEach(field => {
        const normalizedLabel = field.label
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]/g, '');
        const normalizedApiName = field.value
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]/g, '');

        // Exact match (highest priority)
        if (
          normalizedCsvHeader === normalizedLabel ||
          normalizedCsvHeader === normalizedApiName
        ) {
          bestMatch = field.value;
          bestScore = 100;
        }
        // Contains match (medium priority)
        else if (bestScore < 80) {
          if (
            normalizedCsvHeader.includes(normalizedLabel) ||
            normalizedLabel.includes(normalizedCsvHeader)
          ) {
            bestMatch = field.value;
            bestScore = 80;
          } else if (
            normalizedCsvHeader.includes(normalizedApiName) ||
            normalizedApiName.includes(normalizedCsvHeader)
          ) {
            bestMatch = field.value;
            bestScore = 70;
          }
        }
        // Starts with match (lower priority)
        else if (bestScore < 60) {
          if (
            normalizedCsvHeader.startsWith(normalizedLabel) ||
            normalizedLabel.startsWith(normalizedCsvHeader)
          ) {
            bestMatch = field.value;
            bestScore = 60;
          } else if (
            normalizedCsvHeader.startsWith(normalizedApiName) ||
            normalizedApiName.startsWith(normalizedCsvHeader)
          ) {
            bestMatch = field.value;
            bestScore = 50;
          }
        }
      });

      // Only auto-match if we have a good score
      if (bestScore >= 50) {
        mapping[csvHeader] = bestMatch;
      } else {
        mapping[csvHeader] = '';
      }
    });

    return mapping;
  };

  // Parse CSV file (RFC 4180: quoted fields may contain commas)
  const parseCSVFile = file => {
    const reader = new FileReader();
    reader.onload = async e => {
      const { headers, dataRows } = parseCSVText(e.target.result);

      if (headers.length === 0) {
        alert('CSV file is empty or contains only comments.');
        return;
      }

      const misalignedRows = dataRows.filter(
        values => values.some(cell => cell.trim()) && values.length > headers.length
      );
      if (misalignedRows.length > 0) {
        alert(
          'Some rows have more values than column headers. If a field contains commas ' +
          '(e.g. address or amount like 1,000.00), wrap the entire value in double quotes in the CSV. ' +
          'Example: "raj bhawan, ajmer, rajasthan, india"'
        );
        return;
      }

      const rawData = dataRows
        .map((values) => {
          if (!values.some(cell => cell.trim())) return null;
          const row = {};
          headers.forEach((header, i) => {
            row[header] = values[i] ?? '';
          });
          return row;
        })
        .filter(row => row !== null);

      // Enforce import limit
      const maxImportRows = 1000;
      if (rawData.length > maxImportRows) {
        alert(`Import limit is ${maxImportRows} records. Please split the file and try again.`);
        console.warn(
          `Import blocked: ${rawData.length} records exceeds limit of ${maxImportRows}.`
        );
        return;
      }

      // Set CSV headers and raw data
      setCsvHeaders(headers);
      setImportData(rawData);

      // Get available fields from object config (preferred) or columns prop (fallback)
      let fields = await getAllImportFields();
      if (fields.length === 0) {
        fields = columns.map(col => ({
          value: col.field,
          label: col.label,
          type: col.type || 'text',
          is_unique: false, // Default to false for fallback columns
        }));
      }

      // Filter out non-importable fields based on object config
      fields = fields.filter(isImportableField);

      setAvailableFields(fields);

      // Identify primary/unique fields for upsert
      // Only show fields that are primary key (id) or have unique constraint
      const primaryUniqueFields = fields.filter(
        field => field.value === 'id' || field.is_unique === true
      );
      setUpsertFields(primaryUniqueFields);

      // Auto-match fields
      const autoMapping = autoMatchFields(headers, fields);
      setFieldMapping(autoMapping);

      // Show mapping step
      setShowMappingStep(true);
    };
    reader.readAsText(file);
  };

  // Parse Excel file (simplified - you might want to use a library like xlsx)
  const parseExcelFile = () => {
    // For now, we'll show a placeholder message
    // In a real implementation, you'd use a library like 'xlsx' to parse Excel files
    // alert('Excel file parsing requires additional library. Please use CSV format for now.');
    console.warn('Excel file parsing requires additional library. Please use CSV format for now.');
    setImportData([]);
  };

  // Generate and download sample CSV file with importable field headers
  const downloadSampleCSV = async () => {
    // Get all importable fields (same filtering logic as in parseCSVFile)
    let fields = await getAllImportFields();
    if (fields.length === 0) {
      fields = columns.map(col => ({
        value: col.field,
        label: col.label,
        type: col.type || 'text',
        is_unique: false,
      }));
    }

    // Filter out non-importable fields
    const importableFields = fields.filter(field =>
      isImportableField(field, { includeId: importMode !== 'insert' })
    );

    // Generate CSV headers (use field labels, add asterisk for required fields)
    const headerCells = importableFields.map(field => {
      const label = field.label || field.value;
      return escapeCsvValue(isRequiredField(field) ? `${label} *` : label);
    });

    // Generate sample row with placeholder data based on field type
    const sampleCells = importableFields.map(field => {
      const fieldType = (field.data_type || field.type || 'text').toLowerCase();

      switch (fieldType) {
        case 'text':
        case 'textarea':
        case 'longtext':
          return escapeCsvValue('Sample Street, City, State');
        case 'email':
          return 'sample@example.com';
        case 'phone':
          return '1234567890';
        case 'number':
        case 'integer':
          return '100';
        case 'decimal':
        case 'currency':
          return escapeCsvValue('1,000.00');
        case 'date':
          return '2024-01-01';
        case 'datetime':
          return '2024-01-01 12:00:00';
        case 'checkbox':
        case 'boolean':
          return 'true';
        case 'picklist':
        case 'dropdown':
          return 'Option1';
        case 'url':
          return 'https://example.com';
        case 'lookup':
        case 'uuid':
          return 'Enter ID or Unique Value';
        case 'autonumber':
          return 'Auto Generated';
        default:
          return 'Sample Value';
      }
    });

    // Count required fields
    const requiredFieldsCount = importableFields.filter(isRequiredField).length;

    // Add note about required fields if any exist
    let csvContent = '';
    if (requiredFieldsCount > 0) {
      csvContent = `# NOTE: Fields marked with asterisk (*) are REQUIRED and must have values\n`;
      csvContent += `# Total Required Fields: ${requiredFieldsCount}\n`;
      csvContent += `# You can delete the sample row and keep only the header for upload\n`;
      csvContent += `#\n`;
    } else {
      csvContent = `# You can delete the sample row and keep only the header for upload\n`;
    }

    // Combine headers and sample row
    csvContent += `${headerCells.join(',')}\n${sampleCells.join(',')}`;

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${objectName}_import_sample.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle file selection for import
  const handleFileSelect = event => {
    const file = event.target.files[0];
    if (!file) return;

    setImportFileName(file.name);

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv', // .csv
      'application/csv', // .csv alternative
    ];

    if (!validTypes.includes(file.type)) {
      // alert('Please select a valid Excel (.xlsx, .xls) or CSV file.');
      console.error('Please select a valid Excel (.xlsx, .xls) or CSV file.');
      return;
    }

    // Parse file based on type
    if (file.type.includes('csv')) {
      parseCSVFile(file);
    } else {
      parseExcelFile(file);
    }

    // Show mode selection step
    setShowModeStep(true);
  };

  // Handle import mode selection - auto-advance for update/upsert
  const handleModeChange = mode => {
    setImportMode(mode);

    // Auto-advance to upsert field selection for update/upsert modes
    if (mode === 'update' || mode === 'upsert') {
      setShowModeStep(false);
      setShowUpsertStep(true);
    }
  };

  // Handle mode selection confirmation (for insert mode)
  const handleModeSelection = () => {
    setShowModeStep(false);

    // For insert mode, go directly to field mapping
    if (importMode === 'insert') {
      setShowMappingStep(true);
    }
  };

  // Navigation functions
  const goBackToModeSelection = () => {
    setShowUpsertStep(false);
    setShowMappingStep(false);
    setShowModeStep(true);
  };

  const goBackToUpsertSelection = () => {
    setShowMappingStep(false);
    setShowUpsertStep(true);
  };

  const proceedToFieldMapping = () => {
    setShowUpsertStep(false);
    setShowMappingStep(true);
  };

  // Handle field mapping change
  const handleFieldMappingChange = (csvHeader, selectedField) => {
    setFieldMapping(prev => ({
      ...prev,
      [csvHeader]: selectedField,
    }));
  };

  // Handle upsert field selection (single selection - radio button behavior)
  const handleUpsertFieldChange = (fieldValue, isSelected) => {
    setUpsertFields(prev =>
      prev.map(field => ({
        ...field,
        selected: field.value === fieldValue ? isSelected : false,
      }))
    );
  };

  // Apply field mapping to data
  const applyFieldMapping = () => {
    const mappedData = importData.map(row => {
      const mappedRow = {};
      Object.keys(fieldMapping).forEach(csvHeader => {
        const targetField = fieldMapping[csvHeader];
        if (targetField && row[csvHeader] !== undefined) {
          mappedRow[targetField] = {
            value: row[csvHeader],
            type:
              availableFields.find(f => f.value === targetField)?.type ||
              'text',
          };
        }
      });
      return mappedRow;
    });
    return mappedData;
  };

  const downloadImportResultsCSV = (results) => {
    const successMap = new Map();
    const errorMap = new Map();
    (results?.success || []).forEach(item => {
      if (item?.importIndex !== undefined && item?.importIndex !== null) {
        successMap.set(item.importIndex, item);
      }
    });
    (results?.errors || []).forEach(item => {
      if (item?.importIndex !== undefined && item?.importIndex !== null) {
        errorMap.set(item.importIndex, item);
      }
    });

    const headerRow = ['ID', 'Status', 'Message', ...csvHeaders];
    const rows = importData.map((row, index) => {
      const successItem = successMap.get(index);
      const errorItem = errorMap.get(index);
      const idValue = successItem?.id || '';
      const status = successItem ? 'Success' : errorItem ? 'Error' : 'Unknown';
      const message = successItem
        ? `Record ${successItem.action || 'processed'}`
        : errorItem?.error || '';
      const dataColumns = csvHeaders.map(header => row?.[header] ?? '');
      return [idValue, status, message, ...dataColumns];
    });

    const csvContent = [
      headerRow.map(escapeCsvValue).join(','),
      ...rows.map(row => row.map(escapeCsvValue).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const fullFilename = `${objectName}_import_results_${timestamp}.csv`;
    link.href = URL.createObjectURL(blob);
    link.download = fullFilename;
    link.click();
  };

  const resetImportState = () => {
    setImportData([]);
    setImportFileName('');
    setImportProgress({
      total: 0,
      processed: 0,
      created: 0,
      updated: 0,
      errors: 0,
    });
    setImportResult(null);
    setIsImporting(false);
    setCsvHeaders([]);
    setFieldMapping({});
    setShowMappingStep(false);
    setAvailableFields([]);
    setUpsertFields([]);
    setShowUpsertStep(false);
    setShowModeStep(false);
    setImportMode('insert');
    stopAsyncPolling();
    setAsyncJob(null);
    setAsyncCancelling(false);
  };

  const stopAsyncPolling = () => {
    if (asyncPollRef.current) {
      clearInterval(asyncPollRef.current);
      asyncPollRef.current = null;
    }
  };

  const jobApi = () => new DataApi('import-jobs');

  /** Queue the whole import as a background job; progress is polled below. */
  const startAsyncImport = async (records, options) => {
    const { batchSize: _ignored, ...asyncOptions } = options;
    const response = await jobApi().post(`/${objectName}`, {
      records,
      options: asyncOptions,
    });
    const data = response?.data?.data;
    if (!response?.data?.success || !data?.jobId) {
      throw new Error(response?.data?.message || 'Failed to queue import');
    }
    setAsyncJob({
      id: data.jobId,
      total: data.total,
      processed: 0,
      created: 0,
      updated: 0,
      errors: 0,
      skipped: 0,
      status: data.status,
      phase: 'queued',
      notifyEmail: data.notifyEmail,
      message: data.message,
    });
  };

  const handleAsyncCancel = async () => {
    if (!asyncJob?.id || asyncCancelling) return;
    setAsyncCancelling(true);
    try {
      await jobApi().post(`/${asyncJob.id}/cancel`, {});
    } catch (err) {
      console.error('Cancel import job failed:', err);
      setAsyncCancelling(false);
    }
  };

  const downloadAsyncErrorsCsv = async () => {
    if (!asyncJob?.id) return;
    try {
      const response = await jobApi().get(`/${asyncJob.id}/errors.csv`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `import-errors-${asyncJob.id}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download import errors failed:', err);
      alert('Could not download the error file. Please try again.');
    }
  };

  const handleImportComplete = () => {
    resetImportState();
    if (onClose) {
      onClose();
    }
    if (onRefresh) {
      onRefresh();
    }
  };

  // Handle import confirmation
  const handleImportConfirm = async () => {
    if (importData.length === 0) {
      console.warn('No data to import.');
      return;
    }

    // Validate field mapping
    const mappedFields = Object.values(fieldMapping).filter(f => f !== '');
    if (mappedFields.length === 0) {
      alert('Please map at least one field before importing.');
      console.warn('Please map at least one field before importing.');
      return;
    }

    // Validate required fields are mapped
    const requiredFields = availableFields.filter(isRequiredField);
    const mappedFieldValues = Object.values(fieldMapping).filter(f => f !== '');
    const unmappedRequiredFields = requiredFields.filter(
      reqField => !mappedFieldValues.includes(reqField.value)
    );

    if (unmappedRequiredFields.length > 0) {
      const fieldNames = unmappedRequiredFields.map(f => f.label || f.value).join(', ');
      alert(
        `Required fields must be mapped!\n\nThe following required fields are not mapped:\n${fieldNames}\n\nPlease map these fields before importing.`
      );
      console.warn('Unmapped required fields:', unmappedRequiredFields);
      return;
    }

    // Validate upsert fields selection only for update/upsert modes
    const selectedUpsertFields = upsertFields.filter(f => f.selected);
    if (
      (importMode === 'update' || importMode === 'upsert') &&
      selectedUpsertFields.length !== 1
    ) {
      console.warn('Please select exactly one field to use for identifying existing records.');
      return;
    }

    const batchSize = 25;
    setIsImporting(true);
    setImportResult(null);
    setImportProgress({
      total: importData.length,
      processed: 0,
      created: 0,
      updated: 0,
      errors: 0,
    });

    try {
      const mappedData = applyFieldMapping();

      const records = mappedData.map((record, index) => {
        const apiRecord = {};
        Object.keys(record).forEach(fieldName => {
          const field = record[fieldName];
          if (field && typeof field === 'object' && field.value !== undefined) {
            let val = field.value;
            const type = (field.type || '').toLowerCase();

            if (val && (type === 'datetime' || type === 'datetime2')) {
              try {
                // Parse the string as local time and convert to ISO so the backend treats it as the user's intent.
                const dateObj = new Date(val);
                if (!Number.isNaN(dateObj.getTime())) {
                  val = dateObj.toISOString();
                }
              } catch (e) {
                // ignore, send raw
              }
            } else if (val && type === 'date') {
              try {
                // Just format as YYYY-MM-DD using local methods to avoid timezone shift
                const dateObj = new Date(val);
                if (!Number.isNaN(dateObj.getTime())) {
                  const yyyy = dateObj.getFullYear();
                  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
                  const dd = String(dateObj.getDate()).padStart(2, '0');
                  val = `${yyyy}-${mm}-${dd}`;
                }
              } catch (e) {
                // ignore
              }
            }

            apiRecord[fieldName] = val;
          }
        });
        apiRecord.__importIndex = index;
        return apiRecord;
      });

      const dataApi = new DataApi('object');
      const options = {
        mode: importMode,
        batchSize,
      };

      if (importMode === 'update' || importMode === 'upsert') {
        options.upsertFields = [selectedUpsertFields[0].value];
      }

      if (records.length >= IMPORT_ASYNC_THRESHOLD) {
        await startAsyncImport(records, options);
        return;
      }

      const aggregate = {
        total: records.length,
        created: 0,
        updated: 0,
        success: [],
        errors: [],
      };
      for (let i = 0; i < records.length; i += batchSize) {
        const batch = records.slice(i, i + batchSize);
        const isFinalBatch = i + batchSize >= records.length;

        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 750));
        }

        try {
          const response = await dataApi.post(`/${objectName}/bulk-import`, {
            records: batch,
            options: {
              ...options,
              skipPostProcess: !isFinalBatch,
              skipDeferredWorkflows: !isFinalBatch,
              priorImportSuccess: isFinalBatch ? aggregate.success : [],
            },
          });

          if (!response?.data?.success) {
            const msg =
              response?.data?.message || 'Import batch failed';
            batch.forEach(record => {
              aggregate.errors.push({
                record,
                error: msg,
                importIndex: record.__importIndex,
              });
            });
          } else {
            const result = response.data.data || {};
            aggregate.created += result.created || 0;
            aggregate.updated += result.updated || 0;
            if (result.success?.length) {
              aggregate.success.push(
                ...result.success.map(toImportSuccessSummary)
              );
            }
            if (result.errors?.length) {
              aggregate.errors.push(...result.errors);
            }
          }
        } catch (batchError) {
          const msg =
            batchError.response?.data?.message ||
            batchError.response?.data?.error ||
            batchError.message ||
            'Import batch failed';
          batch.forEach(record => {
            aggregate.errors.push({
              record,
              error: msg,
              importIndex: record.__importIndex,
            });
          });
        }

        setImportProgress({
          total: records.length,
          processed: Math.min(i + batch.length, records.length),
          created: aggregate.created,
          updated: aggregate.updated,
          errors: aggregate.errors.length,
        });
      }

      downloadImportResultsCSV({
        success: aggregate.success,
        errors: aggregate.errors,
      });

      setImportResult({
        total: aggregate.total,
        created: aggregate.created,
        updated: aggregate.updated,
        errors: aggregate.errors,
        apiError: null,
      });
    } catch (error) {
      console.error('Import error:', error);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Error importing records. Please try again.';
      setImportResult({
        total: importData.length,
        created: 0,
        updated: 0,
        errors: [],
        apiError: msg,
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportCancel = () => {
    if (isImporting) {
      return;
    }
    if (asyncJob && !importResult) {
      // Job keeps running on the server; the user gets an email when it finishes.
      resetImportState();
      if (onClose) onClose();
      if (onRefresh) onRefresh();
      return;
    }
    if (importResult) {
      handleImportComplete();
      return;
    }
    resetImportState();
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (!show) {
      resetImportState();
    }
  }, [show]);

  // Poll background import job progress
  useEffect(() => {
    if (!asyncJob?.id) return undefined;
    const jobId = asyncJob.id;
    const poll = async () => {
      try {
        const response = await jobApi().get(`/${jobId}`);
        const job = response?.data?.data;
        if (!job) return;
        setAsyncJob(prev => (prev && prev.id === jobId ? { ...prev, ...job } : prev));
        if (IMPORT_JOB_TERMINAL.includes(job.status)) {
          stopAsyncPolling();
          setImportResult({
            async: true,
            jobId,
            status: job.status,
            total: job.total,
            created: job.created,
            updated: job.updated,
            errors: [],
            errorCount: job.errors,
            skipped: job.skipped,
            errorSummary: job.errorSummary,
            apiError: null,
          });
        }
      } catch (err) {
        console.error('Import job poll failed:', err);
      }
    };
    stopAsyncPolling();
    asyncPollRef.current = setInterval(poll, IMPORT_JOB_POLL_MS);
    poll();
    return () => stopAsyncPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncJob?.id]);

  if (!show) {
    return null;
  }

  return (
    <Modal
      show={show}
      onHide={handleImportCancel}
      size='lg'
      centered
      backdrop={isImporting ? 'static' : true}
      keyboard={!isImporting}
    >
      <Modal.Header closeButton={!isImporting}>
        <Modal.Title>
          <i className='fas fa-file-import me-2'></i>
          {importResult ? 'Import Result' : 'Import Data'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {asyncJob && !importResult ? (
          <div className='text-center py-4'>
            <div className='spinner-border text-success mb-3' role='status'>
              <span className='visually-hidden'>Importing...</span>
            </div>
            <p className='mb-2'>
              {asyncJob.phase === 'post_processing'
                ? 'Finishing up (workflows, scores, rollups)...'
                : asyncJob.status === 'queued'
                  ? `Import of ${asyncJob.total} records queued...`
                  : `Importing ${asyncJob.total} records in the background...`}
            </p>
            <div className='progress mb-2' style={{ height: '8px' }}>
              <div
                className='progress-bar bg-success'
                style={{
                  width: `${asyncJob.total ? Math.round(((asyncJob.processed || 0) / asyncJob.total) * 100) : 0}%`,
                }}
              />
            </div>
            <div className='small text-muted mb-3'>
              Processed {asyncJob.processed || 0} / {asyncJob.total} · Created {asyncJob.created || 0} ·
              Updated {asyncJob.updated || 0} · Failed {asyncJob.errors || 0}
            </div>
            <p className='small mb-3'>
              You can close this window — the import keeps running
              {asyncJob.notifyEmail ? ` and we'll email ${asyncJob.notifyEmail} when it's done.` : '.'}
            </p>
            <Button
              variant='outline-danger'
              size='sm'
              onClick={handleAsyncCancel}
              disabled={asyncCancelling || asyncJob.phase === 'post_processing'}
            >
              {asyncCancelling ? 'Cancelling...' : 'Cancel import'}
            </Button>
          </div>
        ) : isImporting ? (
          <div className='text-center py-4'>
            <div className='spinner-border text-success mb-3' role='status'>
              <span className='visually-hidden'>Importing...</span>
            </div>
            <p className='mb-2'>
              Importing {importProgress.total} record
              {importProgress.total !== 1 ? 's' : ''}...
            </p>
            <div className='small text-muted'>
              Processed {importProgress.processed} / {importProgress.total} · Created{' '}
              {importProgress.created} · Updated {importProgress.updated} · Failed{' '}
              {importProgress.errors}
            </div>
          </div>
        ) : importResult ? (
          <div className='import-result'>
            {importResult.apiError ? (
              <div className='alert alert-danger mb-0'>{importResult.apiError}</div>
            ) : (
              <>
                <div className='text-center mb-3'>
                  <i
                    className='fas fa-check-circle text-success'
                    style={{ fontSize: '2.5rem' }}
                  />
                </div>
                <ul className='list-unstyled mb-0'>
                  <li>
                    <strong>Total:</strong> {importResult.total}
                  </li>
                  <li>
                    <strong>Created:</strong> {importResult.created}
                  </li>
                  <li>
                    <strong>Updated:</strong> {importResult.updated}
                  </li>
                  {(importResult.errorCount ?? importResult.errors.length) > 0 && (
                    <li>
                      <strong>Failed:</strong> {importResult.errorCount ?? importResult.errors.length}
                    </li>
                  )}
                  {importResult.skipped > 0 && (
                    <li>
                      <strong>Skipped:</strong> {importResult.skipped}
                    </li>
                  )}
                </ul>
                {importResult.errors.length > 0 && (
                  <div className='mt-3'>
                    <strong className='small'>Errors (first 10):</strong>
                    <ul className='small text-muted mb-0 mt-1'>
                      {importResult.errors.slice(0, 10).map((e, idx) => (
                        <li key={idx}>
                          Row {(e.importIndex ?? idx) + 1}: {e.error}
                        </li>
                      ))}
                      {importResult.errors.length > 10 && (
                        <li className='text-muted'>
                          ... and {importResult.errors.length - 10} more
                        </li>
                      )}
                    </ul>
                  </div>
                )}
                {importResult.async ? (
                  <>
                    {importResult.status !== 'completed' && (
                      <div className='alert alert-warning small mt-3 mb-0'>
                        Import {importResult.status}
                        {importResult.errorSummary ? `: ${importResult.errorSummary}` : '.'}
                      </div>
                    )}
                    {(importResult.errorCount || 0) + (importResult.skipped || 0) > 0 && (
                      <Button
                        variant='outline-secondary'
                        size='sm'
                        className='mt-3'
                        onClick={downloadAsyncErrorsCsv}
                      >
                        Download failed rows (CSV)
                      </Button>
                    )}
                  </>
                ) : (
                  <p className='small text-muted mt-3 mb-0'>
                    A results CSV has been downloaded to your device.
                  </p>
                )}
              </>
            )}
          </div>
        ) : importData.length === 0 ? (
          <div className='text-center py-4'>
            <div className='mb-3'>
              <i
                className='fas fa-cloud-upload-alt'
                style={{ fontSize: '3rem', color: '#6c757d' }}
              ></i>
            </div>
            <h5>Select File to Import</h5>
            <p className='text-muted'>Choose a CSV file to import data</p>

            {/* Download Sample CSV Button */}
            <div className='mb-3'>
              <Button
                variant='outline-success'
                size='sm'
                onClick={downloadSampleCSV}
                className='me-2'
              >
                <i className='fas fa-download me-2'></i>
                Download Sample CSV
              </Button>
              <small className='text-muted d-block mt-2'>
                <i className='fas fa-info-circle me-1'></i>
                Download a sample file with all importable field headers and example data.
                <br />
                <i className='fas fa-asterisk me-1 text-danger' style={{ fontSize: '0.7em' }}></i>
                Fields marked with asterisk (*) are required by the object and must have values.
                <br />
                Values that contain commas must be wrapped in double quotes (e.g. &quot;raj bhawan, ajmer, india&quot;).
              </small>
            </div>

            <div className='mt-3'>
              <input
                type='file'
                accept='.csv'
                onChange={handleFileSelect}
                className='form-control'
                id='importFileInput'
              />
            </div>
            <div className='mt-3'>
              <small className='text-muted'>
                <strong>Supported formats:</strong> CSV (.csv)
              </small>
            </div>
          </div>
        ) : showModeStep ? (
          <div>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <h6>Select Import Mode</h6>
              <span className='text-muted'>{importFileName}</span>
            </div>

            <div className='mb-4'>
              <p className='text-muted'>
                Choose how you want to handle the imported data:
              </p>
            </div>

            <div className='row'>
              <div className='col-md-4'>
                <div
                  className={`card h-100 cursor-pointer ${importMode === 'insert' ? 'border-primary bg-light' : ''}`}
                  onClick={() => handleModeChange('insert')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className='card-body text-center'>
                    <i
                      className='fas fa-plus-circle text-success mb-3'
                      style={{ fontSize: '2rem' }}
                    ></i>
                    <h6 className='card-title'>Insert Only</h6>
                    <p className='card-text small text-muted'>
                      Create new records only. Skip if record already exists.
                    </p>
                  </div>
                </div>
              </div>

              <div className='col-md-4'>
                <div
                  className={`card h-100 cursor-pointer ${importMode === 'update' ? 'border-primary bg-light' : ''}`}
                  onClick={() => handleModeChange('update')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className='card-body text-center'>
                    <i
                      className='fas fa-edit text-warning mb-3'
                      style={{ fontSize: '2rem' }}
                    ></i>
                    <h6 className='card-title'>Update Only</h6>
                    <p className='card-text small text-muted'>
                      Update existing records only. Skip if record not found.
                    </p>
                  </div>
                </div>
              </div>

              <div className='col-md-4'>
                <div
                  className={`card h-100 cursor-pointer ${importMode === 'upsert' ? 'border-primary bg-light' : ''}`}
                  onClick={() => handleModeChange('upsert')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className='card-body text-center'>
                    <i
                      className='fas fa-sync text-info mb-3'
                      style={{ fontSize: '2rem' }}
                    ></i>
                    <h6 className='card-title'>Upsert</h6>
                    <p className='card-text small text-muted'>
                      Update if exists, create if not found.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className='mt-4'>
              <div className='alert alert-info'>
                <i className='fas fa-info-circle me-2'></i>
                <strong>Note:</strong> For Update and Upsert modes, you'll
                need to select which fields to use for identifying existing
                records.
              </div>
            </div>
          </div>
        ) : showUpsertStep ? (
          <div>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <h6>Select Fields for Record Identification</h6>
              <span className='text-muted'>{importFileName}</span>
            </div>

            <div className='mb-3'>
              <small className='text-muted'>
                Select which field should be used to identify existing records
                for updates. Only primary key (id) or unique fields are
                available for selection.
              </small>
            </div>

            <div className='table-responsive'>
              <Table striped hover size='sm'>
                <thead className='table-dark'>
                  <tr>
                    <th style={{ width: '60px' }}>Select</th>
                    <th style={{ width: '40%' }}>Field Name</th>
                    <th style={{ width: '60%' }}>Field Label</th>
                  </tr>
                </thead>
                <tbody>
                  {upsertFields.map((field, index) => (
                    <tr key={index}>
                      <td>
                        <Form.Check
                          type='radio'
                          name='upsertField'
                          checked={field.selected || false}
                          onChange={e =>
                            handleUpsertFieldChange(
                              field.value,
                              e.target.checked
                            )
                          }
                        />
                      </td>
                      <td>
                        <strong>{field.value}</strong>
                      </td>
                      <td>
                        {field.label}
                        <br />
                        <small className='text-muted'>({field.type})</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        ) : showMappingStep ? (
          <div>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <h6>Map CSV Columns to Fields</h6>
              <span className='text-muted'>{importFileName}</span>
            </div>

            <div className='mb-3'>
              <small className='text-muted'>
                Map each CSV column to the corresponding field in your object.
                Unmapped columns will be ignored during import.
                <br />
                <i className='fas fa-magic me-1 text-success'></i>
                <strong>Auto-matched fields</strong> are highlighted in green.
              </small>
            </div>

            {/* Show Required Fields Alert */}
            {(() => {
              const requiredFields = availableFields.filter(isRequiredField);
              if (requiredFields.length > 0) {
                const mappedFieldValues = Object.values(fieldMapping).filter(f => f !== '');
                const unmappedRequired = requiredFields.filter(
                  reqField => !mappedFieldValues.includes(reqField.value)
                );

                return (
                  <div className={`alert ${unmappedRequired.length > 0 ? 'alert-warning' : 'alert-success'} mb-3`}>
                    <div className='d-flex align-items-start'>
                      <i className={`fas ${unmappedRequired.length > 0 ? 'fa-exclamation-triangle' : 'fa-check-circle'} me-2 mt-1`}></i>
                      <div className='flex-grow-1'>
                        <strong>Required Fields in {objectName}:</strong>
                        <small className='d-block text-muted mb-2'>
                          (Based on object configuration - these fields must be mapped)
                        </small>
                        <div className='mt-2'>
                          {requiredFields.map((field) => {
                            const isMapped = mappedFieldValues.includes(field.value);
                            return (
                              <span
                                key={field.value}
                                className={`badge me-2 mb-1 ${isMapped ? 'bg-success' : 'bg-danger'}`}
                                style={{ fontSize: '0.85em' }}
                              >
                                <i className={`fas ${isMapped ? 'fa-check' : 'fa-times'} me-1`}></i>
                                {field.label || field.value}
                              </span>
                            );
                          })}
                        </div>
                        {unmappedRequired.length > 0 && (
                          <small className='d-block mt-2 text-danger'>
                            <strong>⚠️ {unmappedRequired.length} required field(s) not yet mapped.</strong> Please map them before importing.
                          </small>
                        )}
                        {unmappedRequired.length === 0 && (
                          <small className='d-block mt-2 text-success'>
                            <strong>✓ All required fields are mapped!</strong> You're ready to import.
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            <div className='mb-3 d-flex justify-content-between align-items-center'>
              <div>
                <Button
                  variant='outline-secondary'
                  size='sm'
                  onClick={() => {
                    const clearedMapping = {};
                    csvHeaders.forEach(header => {
                      clearedMapping[header] = '';
                    });
                    setFieldMapping(clearedMapping);
                  }}
                >
                  <i className='fas fa-times me-2'></i>
                  Clear All
                </Button>
                <Button
                  variant='outline-primary'
                  size='sm'
                  className='ms-2'
                  onClick={() => {
                    const autoMapping = autoMatchFields(
                      csvHeaders,
                      availableFields
                    );
                    setFieldMapping(autoMapping);
                  }}
                >
                  <i className='fas fa-magic me-2'></i>
                  Auto-Match Again
                </Button>
              </div>
              <div className='text-end'>
                <small className='text-muted d-block'>
                  {Object.values(fieldMapping).filter(f => f !== '').length}{' '}
                  of {csvHeaders.length} CSV columns mapped
                </small>
                {(() => {
                  const requiredFields = availableFields.filter(isRequiredField);
                  const mappedFieldValues = Object.values(fieldMapping).filter(f => f !== '');
                  const mappedRequiredCount = requiredFields.filter(
                    reqField => mappedFieldValues.includes(reqField.value)
                  ).length;

                  if (requiredFields.length > 0) {
                    return (
                      <small className={mappedRequiredCount === requiredFields.length ? 'text-success' : 'text-danger'}>
                        <i className={`fas ${mappedRequiredCount === requiredFields.length ? 'fa-check-circle' : 'fa-exclamation-circle'} me-1`}></i>
                        {mappedRequiredCount} of {requiredFields.length} required fields mapped
                      </small>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            <div className='table-responsive'>
              <Table striped hover size='sm'>
                <thead className='table-dark'>
                  <tr>
                    <th style={{ width: '40%' }}>CSV Column</th>
                    <th style={{ width: '60%' }}>Map to Field</th>
                  </tr>
                </thead>
                <tbody>
                  {csvHeaders.map((header, index) => {
                    const isAutoMatched = fieldMapping[header] !== '';
                    const matchedField = availableFields.find(
                      f => f.value === fieldMapping[header]
                    );

                    return (
                      <tr
                        key={index}
                        className={isAutoMatched ? 'table-success' : ''}
                        style={
                          isAutoMatched
                            ? { backgroundColor: 'rgba(25, 135, 84, 0.1)' }
                            : {}
                        }
                      >
                        <td>
                          <div className='d-flex align-items-center'>
                            <strong>{header}</strong>
                            {isAutoMatched && (
                              <i
                                className='fas fa-magic text-success ms-2'
                                title='Auto-matched'
                              ></i>
                            )}
                          </div>
                          <br />
                          <small className='text-muted'>
                            Sample: {importData[0]?.[header] || 'N/A'}
                          </small>
                        </td>
                        <td>
                          <Form.Select
                            size='sm'
                            value={fieldMapping[header] || ''}
                            onChange={e =>
                              handleFieldMappingChange(header, e.target.value)
                            }
                            className={isAutoMatched ? 'border-success' : ''}
                          >
                            <option value=''>-- Select Field --</option>
                            {availableFields.map(field => (
                              <option key={field.value} value={field.value}>
                                {field.label} ({field.type}){field.is_required ? ' *Required' : ''}
                              </option>
                            ))}
                          </Form.Select>
                          {isAutoMatched && matchedField && (
                            <small className='text-success d-block mt-1'>
                              <i className='fas fa-check-circle me-1'></i>
                              Auto-matched to "{matchedField.label}"
                              {matchedField.is_required && (
                                <span className='text-danger ms-2'>
                                  <i className='fas fa-exclamation-circle me-1'></i>
                                  Required
                                </span>
                              )}
                            </small>
                          )}
                          {!isAutoMatched && fieldMapping[header] && matchedField?.is_required && (
                            <small className='text-danger d-block mt-1'>
                              <i className='fas fa-exclamation-circle me-1'></i>
                              This is a required field - must have values
                            </small>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {/* Only show upsert configuration for update/upsert modes */}
            {(importMode === 'update' || importMode === 'upsert') && (
              <div className='mt-3 d-flex justify-content-end'>
                <Button
                  variant='outline-primary'
                  size='sm'
                  onClick={() => setShowUpsertStep(true)}
                >
                  <i className='fas fa-arrow-right me-2'></i>
                  Configure Upsert Fields
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <h6>Preview Mapped Data ({importData.length} records)</h6>
              <div className='d-flex align-items-center gap-2'>
                <Button
                  variant='outline-secondary'
                  size='sm'
                  onClick={() => setShowMappingStep(true)}
                >
                  <i className='fas fa-edit me-2'></i>
                  Edit Mapping
                </Button>
                <span className='text-muted'>{importFileName}</span>
              </div>
            </div>

            <div
              className='table-responsive'
              style={{ maxHeight: '400px', overflowY: 'auto' }}
            >
              <Table striped hover size='sm'>
                <thead className='table-dark'>
                  <tr>
                    {Object.keys(applyFieldMapping()[0] || {}).map(
                      (key, index) => (
                        <th key={index}>
                          {availableFields.find(f => f.value === key)
                            ?.label || key}
                          <br />
                          <small className='text-muted'>({key})</small>
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {applyFieldMapping()
                    .slice(0, 10)
                    .map((row, index) => (
                      <tr key={index}>
                        {Object.values(row).map((field, fieldIndex) => (
                          <td key={fieldIndex}>
                            {field && typeof field === 'object'
                              ? field.value
                              : field}
                          </td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>

            {importData.length > 10 && (
              <div className='text-center mt-2'>
                <small className='text-muted'>
                  Showing first 10 records of {importData.length} total
                  records
                </small>
              </div>
            )}

            <div className='mt-3 p-3 bg-light rounded'>
              <h6 className='mb-2'>Field Mapping Summary:</h6>
              <div className='row'>
                {Object.entries(fieldMapping).map(
                  ([csvHeader, mappedField]) =>
                    mappedField && (
                      <div key={csvHeader} className='col-md-6 mb-1'>
                        <small>
                          <strong>{csvHeader}</strong> →{' '}
                          {availableFields.find(f => f.value === mappedField)
                            ?.label || mappedField}
                        </small>
                      </div>
                    )
                )}
              </div>
            </div>

            <div className='mt-3 p-3 bg-info bg-opacity-10 rounded'>
              <h6 className='mb-2'>
                Upsert Fields (for record identification):
              </h6>
              <div className='row'>
                {upsertFields
                  .filter(f => f.selected)
                  .map((field, index) => (
                    <div key={index} className='col-md-6 mb-1'>
                      <small>
                        <i className='fas fa-key text-warning me-1'></i>
                        <strong>{field.label}</strong> ({field.value})
                      </small>
                    </div>
                  ))}
              </div>
              {upsertFields.filter(f => f.selected).length === 0 && (
                <small className='text-muted'>
                  No upsert fields selected
                </small>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {importResult ? (
          <Button variant='primary' onClick={handleImportComplete}>
            OK
          </Button>
        ) : asyncJob ? (
          <Button variant='secondary' onClick={handleImportCancel}>
            Close (import continues)
          </Button>
        ) : (
          <>
        <Button
          variant='secondary'
          onClick={handleImportCancel}
          disabled={isImporting}
        >
          Cancel
        </Button>

        {/* Back Button - Show for all steps except file selection */}
        {importData.length > 0 && !isImporting && (
          <Button
            variant='outline-secondary'
            onClick={() => {
              if (showModeStep) {
                resetImportState();
                return;
              }
              if (showUpsertStep) {
                goBackToModeSelection();
              } else if (showMappingStep) {
                if (importMode === 'insert') {
                  goBackToModeSelection();
                } else {
                  goBackToUpsertSelection();
                }
              }
            }}
            disabled={isImporting}
          >
            <i className='fas fa-arrow-left me-2'></i>
            Back
          </Button>
        )}

        {/* Next/Import Button */}
        {showModeStep && importMode === 'insert' && (
          <Button
            variant='primary'
            onClick={handleModeSelection}
            disabled={isImporting}
          >
            <i className='fas fa-arrow-right me-2'></i>
            Next
          </Button>
        )}

        {showUpsertStep && (
          <Button
            variant='primary'
            onClick={proceedToFieldMapping}
            disabled={isImporting}
          >
            <i className='fas fa-arrow-right me-2'></i>
            Next: Field Mapping
          </Button>
        )}

        {!showModeStep && !showUpsertStep && importData.length > 0 && (
          <Button
            variant='success'
            onClick={handleImportConfirm}
            disabled={isImporting}
          >
            {isImporting ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                ></span>
                Importing...
              </>
            ) : (
              <>
                <i className='fas fa-check me-2'></i>
                Import {importData.length} Records
              </>
            )}
          </Button>
        )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ImportDataModal;
